/** Voicemail list from the server (metadata from FusionPBX). */
import { useCallback, useEffect, useState } from 'react';

export interface VoicemailMessage {
  id: string;
  receivedAt: number;
  callerName: string;
  callerNumber: string;
  seconds: number;
  isNew: boolean;
  transcription: string;
}

const REFRESH_MS = 60_000;

/**
 * Fetches the list, refetches whenever `refreshKey` changes (MWI pushes) and every minute as a backstop:
 * this PBX has no MWI responder, so SUBSCRIBE is accepted but no initial NOTIFY ever arrives —
 * the list is the source of truth for counts, MWI only makes updates instant.
 */
export function useVoicemails(refreshKey: string) {
  const [messages, setMessages] = useState<VoicemailMessage[] | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/voicemails', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) throw new Error(String(r.status));
      setMessages((await r.json()).messages);
      setError('');
    } catch {
      setError('Voicemail list is unavailable right now.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load, refreshKey]);
  useEffect(() => {
    const t = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);
  const counts = messages ? { new: messages.filter((m) => m.isNew).length, old: messages.filter((m) => !m.isNew).length } : null;
  return { messages, counts, error, reload: load };
}
